import { max, mean, isNumber } from 'lodash';
import { UTCDate } from '@date-fns/utc';
import { add, differenceInCalendarDays, format, parse, startOfHour, sub } from 'date-fns';
import { SECONDARY_COLOR } from 'src/theme';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { HeatmapChart } from 'echarts/charts';
import { TooltipComponent, GridComponent, VisualMapComponent, DataZoomComponent } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import { EnergyEventDto, EnergyUsageTimeseriesDto } from '../../../../domain/energy/interface';
import { BaselineMode } from '../../../../domain/energy/baselineMarket';
import { IDateRange } from '../../../interface';
import { GET_ROW_HEIGHT_TYPE, calculateChartHeight } from '../../../../domain/common/chart';
import { EVENT_COLORS, HOUR_MS, HOURS } from '../../../../domain/commonConst';
import { DEFAULT_DATE_TIME_FORMAT_WITHOUT_ZONE, getComputerFormattedTime } from '../../../../domain/date/date';
import { groupEnergyUsageByHour } from './groupEnergyUsageByHour';

echarts.use([TooltipComponent, GridComponent, CanvasRenderer, HeatmapChart, VisualMapComponent, DataZoomComponent]);

interface IEnergyHeatMapChart {
    dateRange: IDateRange;
    energyData: EnergyUsageTimeseriesDto; // aggregate
    eventsData: EnergyEventDto[]; // aggregate
    baselineMode: BaselineMode;
}

export const EnergyHeatMapChart = ({ energyData, eventsData, dateRange, baselineMode }: IEnergyHeatMapChart) => {
    const { heatMapData, extraDataMap } = prepareEnergyDataForHeatMap({ dateRange, energyData, eventsData, baselineMode });
    const maxUsage = getChartMaxUsageKw(heatMapData);
    const height = getChartHeight(dateRange) + 200;

    const option = {
        legend: {
            data: [
                { name: 'Event', icon: 'rect' },
                { name: 'Pre-Event', icon: 'rect' },
                { name: 'Post-Event', icon: 'rect' },
                { name: 'Price Response', icon: 'rect' },
                { name: 'Clean Response', icon: 'rect' },
            ],
            bottom: 0,
            left: 'center',
            textStyle: {
                fontSize: 12,
            },
        },
        tooltip: {
            formatter: (params: { value: [string, string, number]; marker: string }) => {
                const [timeFormatted, dateFormatted, usage] = params.value || [];

                const key = `${dateFormatted} ${timeFormatted}`;
                const baselineCaiso10d = extraDataMap.get(key)?.baselineCaiso10d;
                const performanceCaiso10d = extraDataMap.get(key)?.performanceCaiso10d;
                const eventCode = extraDataMap.get(key)?.eventCode || null;

                const baselineValue = isNumber(baselineCaiso10d) ? `${baselineCaiso10d} kW` : 'No data';
                const performance = isNumber(performanceCaiso10d) ? `${Math.max(performanceCaiso10d, 0)} kW` : 'No data';
                const qualityOfReadings = extraDataMap.get(key)?.qualityOfReadings || [];

                const performanceFormatted =
                    [null, 'pre-event', 'post-event'].includes(eventCode) === false
                        ? `</br>${params.marker} performance: <strong>${performance}</strong>`
                        : '';

                const datetimeFormatted = `${format(parse(`${dateFormatted} ${timeFormatted}`, 'EEE, dd/LLL/yyyy h-a', new UTCDate()), DEFAULT_DATE_TIME_FORMAT_WITHOUT_ZONE)} ${getComputerFormattedTime(new Date(), 'zzz')}`;

                const eventTypeFormatted = eventCode !== null ? `<center>${eventCode.toUpperCase().replaceAll('-', ' ')} at this hour</center>` : '';

                const qualityOfReadingFormatted = formatQualityOfReading(qualityOfReadings);

                /** baseline/performance can't be aggregated across mixed markets - hide them */
                const baselineFormatted =
                    baselineMode !== 'none' ? `${params.marker} baseline: <strong>${baselineValue}</strong>${performanceFormatted}</br>` : '';

                return `
                    ${datetimeFormatted}
                    </br>
                    ${params.marker} <strong>${usage} kW</strong>
                    </br>
                    ${baselineFormatted}
                    data quality: <strong>${qualityOfReadingFormatted}</strong>
                    </br>
                    ${eventTypeFormatted}
                `;
            },
        },
        grid: {
            top: 120,
            bottom: 80,
            left: '10%',
            right: '10%',
        },
        xAxis: ['top', 'bottom'].map(position => ({
            type: 'category',
            data: HOURS,
            position,
            splitArea: {
                show: true,
            },
            axisLabel: {
                color: '#697475',
                margin: 10,
            },
            axisLine: {
                show: true,
                lineStyle: {
                    color: '#d6dbdb',
                    width: 2,
                    type: 'solid',
                },
            },
            axisTick: {
                show: true,
            },
        })),
        yAxis: {
            type: 'category',
            splitArea: {
                show: true,
            },
            axisLabel: {
                color: '#697475',
                margin: 10,
            },
            axisLine: {
                show: true,
                lineStyle: {
                    color: '#d6dbdb',
                    width: 2,
                    type: 'solid',
                },
            },
            axisTick: {
                show: true,
            },
        },
        visualMap: {
            min: 0,
            max: maxUsage,
            calculable: true,
            orient: 'horizontal',
            left: 'center',
            top: 30,
        },
        itemHeight: 300,
        series: [
            {
                name: 'Event',
                type: 'heatmap',
                data: [],
                itemStyle: {
                    color: EVENT_COLORS.DEFAULT,
                },
            },
            {
                name: 'Pre-Event',
                type: 'heatmap',
                data: [],
                itemStyle: {
                    color: EVENT_COLORS.PRE_EVENT,
                },
            },
            {
                name: 'Post-Event',
                type: 'heatmap',
                data: [],
                itemStyle: {
                    color: EVENT_COLORS.POST_EVENT,
                },
            },
            {
                name: 'Price Response',
                type: 'heatmap',
                data: [],
                itemStyle: {
                    color: EVENT_COLORS.PRICE_RESPONSE,
                },
            },
            {
                name: 'Clean Response',
                type: 'heatmap',
                data: [],
                itemStyle: {
                    color: EVENT_COLORS.CLEAN_RESPONSE,
                },
            },
            {
                name: 'Actual Data',
                type: 'heatmap',
                data: heatMapData,
                label: {
                    show: true,
                },
                emphasis: {
                    itemStyle: {
                        shadowBlur: 5,
                        shadowColor: 'rgba(0, 0, 0, 0.5)',
                    },
                },
            },
        ],
    };

    return (
        <div className="energy-chart-container">
            <div style={{ textAlign: 'center', color: SECONDARY_COLOR, marginTop: 10, paddingBottom: 0 }}>
                Heat map displays total energy usage for all selected sites. Hover over a cell for more information.
            </div>
            <ReactEChartsCore echarts={echarts} option={option} notMerge lazyUpdate style={{ height: `${height}px`, minHeight: '300px' }} />
        </div>
    );
};

function getChartMaxUsageKw(heatMapData: HeatMapData) {
    const DEFAULT_HEAT_MAP_MAX_VALUE = 5000;
    return max(heatMapData.map(it => it.value[2 /** usage index */])) || DEFAULT_HEAT_MAP_MAX_VALUE;
}

function getChartHeight(dateRange: IDateRange) {
    const numberOfRows = differenceInCalendarDays(new UTCDate(dateRange.end), new UTCDate(dateRange.start)) + 1;
    const height = calculateChartHeight(numberOfRows, GET_ROW_HEIGHT_TYPE.INTERVAL_DATA);
    return height;
}

function formatTimeByHour(timestamp: number) {
    return format(new UTCDate(+timestamp), 'h-a');
}

type EventCode = 'pre-event' | 'post-event' | 'event' | 'price-response-event' | 'clean-response-event' | null;

function getChartStyleByEventCode(code: EventCode) {
    switch (code) {
        case 'pre-event':
            return {
                label: {
                    fontWeight: 'bold',
                    backgroundColor: EVENT_COLORS.PRE_EVENT,
                    color: 'white',
                    padding: 10,
                    borderRadius: 2,
                },
            };
        case 'post-event':
            return {
                label: {
                    fontWeight: 'bold',
                    backgroundColor: EVENT_COLORS.POST_EVENT,
                    color: 'white',
                    padding: 10,
                    borderRadius: 2,
                },
            };
        case 'event':
            return {
                label: {
                    backgroundColor: EVENT_COLORS.DEFAULT,
                    color: 'white',
                    padding: 10,
                    borderRadius: 2,
                },
            };
        case 'price-response-event':
            return {
                label: {
                    fontWeight: 'bold',
                    backgroundColor: EVENT_COLORS.PRICE_RESPONSE,
                    color: 'white',
                    padding: 10,
                    borderRadius: 2,
                },
            };
        case 'clean-response-event':
            return {
                label: {
                    fontWeight: 'bold',
                    backgroundColor: EVENT_COLORS.CLEAN_RESPONSE,
                    color: 'white',
                    padding: 10,
                    borderRadius: 2,
                },
            };
        default:
            return {};
    }
}

type HeatMapData = {
    value: [string /** time */, string /** date */, number | null /** usageKw */];
    style?: { label: { fontWeight?: string; backgroundColor: string; color: string } };
}[];

type ExtraHeatMapDataItem = {
    baselineCaiso10d: number | null;
    performanceCaiso10d: number | null;
    eventCode: EventCode;
    qualityOfReadings: number[];
};
type ExtraHeatMapDataMap = Map<string, ExtraHeatMapDataItem>;

function prepareEnergyDataForHeatMap({
    dateRange,
    energyData,
    eventsData,
    baselineMode,
}: {
    dateRange: IDateRange;
    energyData: EnergyUsageTimeseriesDto;
    eventsData: EnergyEventDto[];
    baselineMode: BaselineMode;
}): { heatMapData: HeatMapData; extraDataMap: ExtraHeatMapDataMap } {
    /** map to store extra chart's data (which we display on hover) */
    const extraDataMap = new Map<string, ExtraHeatMapDataItem>();

    const eventsDataHourMap = buildEventsDataHourMap(eventsData);

    /** array to store main chart's data */
    const heatMapData: HeatMapData = [];

    if (!energyData?.values.length) {
        return { heatMapData, extraDataMap };
    }

    const hourMap = groupEnergyUsageByHour(dateRange, energyData, baselineMode);
    if (!hourMap) {
        return { heatMapData, extraDataMap };
    }

    function getFormattedAvgKw(values: number[]): number | null {
        if (!values?.length) return null;

        const numbers = values.filter(v => isNumber(v));
        if (!numbers.length) return null;

        const avg = mean(numbers);

        return Number(avg.toFixed(1));
    }

    hourMap.forEach((item, hourTimestamp) => {
        const usageKwh = getFormattedAvgKw(item.usageKw);
        const baselineCaiso10d = getFormattedAvgKw(item.baselineCaiso10d);
        const performanceCaiso10d = getFormattedAvgKw(item.performanceCaiso10dKw);

        const timeFormatted = formatTimeByHour(hourTimestamp);
        const dateFormatted = format(new UTCDate(hourTimestamp), 'EEE, dd/LLL/yyyy');

        const eventCode = eventsDataHourMap.get(hourTimestamp) ?? null;

        const style = getChartStyleByEventCode(eventCode);

        extraDataMap.set(`${dateFormatted} ${timeFormatted}`, {
            baselineCaiso10d,
            performanceCaiso10d,
            qualityOfReadings: item.qualityOfReading?.filter(isNumber) || [],
            eventCode: eventCode,
        });

        heatMapData.push({
            value: [timeFormatted, dateFormatted, usageKwh],
            ...style,
        });
    });

    return { heatMapData, extraDataMap };
}

function buildEventsDataHourMap(eventsData: EnergyEventDto[]) {
    /** build events map to quicker and easier find data */
    const eventsDataHourMap = new Map<number, EventCode>();

    /** first - add post-event */
    eventsData.forEach(event => {
        if (!event.postDuration) return;

        const eventEndDateTime = add(new Date(event.start), { minutes: event.duration });
        const postStart = startOfHour(new UTCDate(eventEndDateTime)).valueOf();
        const postEnd = add(eventEndDateTime, { minutes: event.postDuration }).valueOf();

        let cursor = postStart;
        while (cursor < postEnd) {
            eventsDataHourMap.set(cursor, 'post-event');
            cursor = cursor + HOUR_MS;
        }
    });

    /** second - add pre-event */
    eventsData.forEach(event => {
        if (!event.preDuration) return;

        const preStart = startOfHour(new UTCDate(sub(new Date(event.start), { minutes: event.preDuration }))).valueOf();
        const preEnd = new Date(event.start).valueOf();

        let cursor = preStart;
        while (cursor < preEnd) {
            eventsDataHourMap.set(cursor, 'pre-event');
            cursor = cursor + HOUR_MS;
        }
    });

    /** third - add event */
    eventsData.forEach(event => {
        const start = startOfHour(new UTCDate(event.start)).valueOf();
        const end = add(new Date(event.start), { minutes: event.duration }).valueOf();

        let cursor = start;
        while (cursor < end) {
            /** during event - our eventCode is event | price-response-event | clean-response-event */
            eventsDataHourMap.set(cursor, event.eventSourceType);
            cursor = cursor + HOUR_MS;
        }
    });

    return eventsDataHourMap;
}

function formatQualityOfReading(qualityOfReadings: number[]): string {
    const maxQuality = Math.max(...qualityOfReadings);

    if (isNumber(maxQuality) && maxQuality === 19) return 'Revenue Quality';
    if (isNumber(maxQuality) && maxQuality <= 18 && maxQuality > 10) return 'Preliminary';
    if (isNumber(maxQuality) && maxQuality <= 10 && maxQuality >= 0) return 'Estimated';

    return 'Missing';
}
